'use client';

import { useState } from 'react';
import { css, cx, stack } from '@bts-web/utils-style-engine';
import { TabBar, typography } from '@bts-web/core-features/common';
import { ContentSectionWrapper } from '../../../../layouts/ContentSectionWrapper/ContentSectionWrapper';
import { anchorScrollMarginClass } from '../AnchorNavigation/anchorNavigationConfig';

export type CompositionItem = {
  name: string;
  percentage: number;
};

export type CompositionTabData = {
  items: CompositionItem[];
  lastUpdated: string;
};

export type ETFCompositionData = {
  industries: CompositionTabData | null;
  companies: CompositionTabData | null;
  countries: CompositionTabData | null;
};

export type AssetDetailsETFCompositionTranslations = {
  composition: string;
  industries: string;
  companies: string;
  countries: string;
  industriesNumber: string;
  companiesNumber: string;
  countriesNumber: string;
  industriesTagDescription: string;
  companiesTagDescription: string;
  countriesTagDescription: string;
  lastUpdateDatetime: string;
  dataProvidedByFactset: string;
  showLess: string;
  showMore: string;
};

type TabKey = 'industries' | 'companies' | 'countries';

const COLLAPSED_COUNT = 5;

// Colors assigned by index — no design tokens since these are data-driven chart colors.
const CHART_COLORS = [
  '#111111',
  '#4ade80',
  '#fb923c',
  '#3b82f6',
  '#facc15',
  '#92400e',
  '#4b5563',
  '#86efac',
  '#7dd3fc',
  '#e879f9',
];

export const MOCK_ETF_COMPOSITION_DATA: ETFCompositionData = {
  industries: {
    items: [
      { name: 'Technology', percentage: 48 },
      { name: 'Telecommunication', percentage: 23 },
      { name: 'Consumer Discretionary', percentage: 15 },
      { name: 'Consumer Staples', percentage: 6 },
      { name: 'Health Care', percentage: 4 },
      { name: 'Industrials', percentage: 1 },
      { name: 'Utilities', percentage: 1 },
      { name: 'Energy', percentage: 1 },
      { name: 'Other', percentage: 1 },
    ],
    lastUpdated: '31.10.2024, 10:00 PM',
  },
  companies: {
    items: [
      { name: 'Apple Inc.', percentage: 22 },
      { name: 'Microsoft Corp', percentage: 18 },
      { name: 'Amazon.com Inc', percentage: 14 },
      { name: 'NVIDIA Corp', percentage: 12 },
      { name: 'Alphabet Inc', percentage: 10 },
      { name: 'Meta Platforms', percentage: 8 },
      { name: 'Tesla Inc', percentage: 6 },
      { name: 'Berkshire Hathaway', percentage: 5 },
      { name: 'Other', percentage: 5 },
    ],
    lastUpdated: '31.10.2024, 10:00 PM',
  },
  countries: {
    items: [
      { name: 'United States', percentage: 65 },
      { name: 'Japan', percentage: 6 },
      { name: 'United Kingdom', percentage: 5 },
      { name: 'France', percentage: 4 },
      { name: 'Canada', percentage: 3 },
      { name: 'Germany', percentage: 3 },
      { name: 'Switzerland', percentage: 2 },
      { name: 'Australia', percentage: 2 },
      { name: 'Netherlands', percentage: 2 },
      { name: 'Other', percentage: 8 },
    ],
    lastUpdated: '31.10.2024, 10:00 PM',
  },
};

function interpolate(
  template: string,
  vars: Record<string, string | number>,
): string {
  return Object.entries(vars).reduce(
    (s, [k, v]) => s.replace(`{{${k}}}`, String(v)),
    template,
  );
}

const countLabelClass = css({ ...typography.bodyMediumBold });

const descriptionClass = css({
  ...typography.captionMedium,
  color: 'neutrals.text_secondary',
});

const barClass = css({
  height: '8px',
  // 8px matches the border-radius token 'small'; no token for height here
  borderRadius: '8px',
  overflow: 'hidden',
  display: 'flex',
  mt: 'small',
});

const listItemClass = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'small',
  py: 'small',
  borderBottom: '1px solid',
  borderColor: 'neutrals.stroke_secondary',
  '&:last-of-type': {
    borderBottom: 'none',
  },
});

const itemNameClass = css({
  ...typography.bodyMedium,
  flex: 1,
});

const pillClass = css({
  ...typography.captionSmall,
  color: 'neutrals.text_secondary',
  backgroundColor: 'neutrals.fill_quinary',
  px: 'extra_small_2',
  py: 'extra_small_3',
  // pill shape: large radius on a small element collapses to a capsule
  borderRadius: '100px',
  minWidth: '42px',
  textAlign: 'center',
});

const footerClass = css({
  ...typography.captionSmall,
  color: 'neutrals.text_secondary',
  mt: 'small',
});

const showToggleClass = css({
  ...typography.bodyMediumBold,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'extra_small_2',
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  p: 0,
  mt: 'medium',
  color: 'neutrals.text_primary',
});

interface AssetDetailsETFCompositionProps {
  translations: AssetDetailsETFCompositionTranslations;
  compositionData?: ETFCompositionData;
  id?: string;
}

export const AssetDetailsETFComposition = ({
  translations,
  compositionData,
  id,
}: AssetDetailsETFCompositionProps) => {
  if (!compositionData) {
    return null;
  }

  const availableTabs = (
    ['industries', 'companies', 'countries'] as TabKey[]
  ).filter((key) => compositionData[key] !== null);

  const [activeTab, setActiveTab] = useState<TabKey>(
    availableTabs[0] ?? 'industries',
  );

  const [expanded, setExpanded] = useState(false);

  if (availableTabs.length === 0) return null;

  const tabItems = availableTabs.map((key) => ({
    label: translations[key],
    value: key,
  }));

  const activeData = compositionData[activeTab];

  if (!activeData) return null;

  const { items, lastUpdated } = activeData;

  const displayedItems = expanded ? items : items.slice(0, COLLAPSED_COUNT);

  const hasMore = items.length > COLLAPSED_COUNT;

  const countKey =
    `${activeTab}Number` as keyof AssetDetailsETFCompositionTranslations;

  const descriptionKey =
    `${activeTab}TagDescription` as keyof AssetDetailsETFCompositionTranslations;

  const countLabel = interpolate(translations[countKey], {
    number: items.length,
  });

  return (
    <ContentSectionWrapper
      aria-label="ETF composition section"
      sectionTitle={translations.composition}
      className={cx(id ? anchorScrollMarginClass : undefined)}
      {...(id ? { id } : {})}
    >
      <TabBar
        tabItems={tabItems}
        value={activeTab}
        onValueChange={(v) => {
          setActiveTab(v as TabKey);

          setExpanded(false);
        }}
      />

      <div className={stack({ gap: 'extra_small_3', paddingTop: 'medium' })}>
        <span className={countLabelClass}>{countLabel}</span>
        <span className={descriptionClass}>{translations[descriptionKey]}</span>
      </div>

      <div className={barClass}>
        {items.map((item, i) => (
          <div
            key={item.name + '_' + items.indexOf(item)}
            style={{
              width: `${item.percentage}%`,
              backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
            }}
          />
        ))}
      </div>

      <div role="list">
        {displayedItems.map((item, i) => (
          <div
            key={item.name + '_' + items.indexOf(item)}
            role="listitem"
            className={listItemClass}
          >
            {/* color is data-driven; '50%' is the documented exception for perfect circles */}
            <span
              aria-hidden="true"
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                flexShrink: 0,
              }}
            />
            <span className={itemNameClass}>{item.name}</span>
            <span className={pillClass}>{item.percentage}%</span>
          </div>
        ))}
      </div>

      {hasMore && (
        <button
          type="button"
          className={showToggleClass}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? translations.showLess : translations.showMore}
          <span aria-hidden="true">{expanded ? '↑' : '↓'}</span>
        </button>
      )}

      <p className={footerClass}>
        {interpolate(translations.lastUpdateDatetime, {
          datetime: lastUpdated,
        })}
      </p>
      <p className={footerClass}>{translations.dataProvidedByFactset}</p>
    </ContentSectionWrapper>
  );
};
